import type {
  CharacterKind,
  HazardKind,
  PickupKind,
  PlayerId,
  PropKind,
} from '@/components'
import { defineResource } from '@/core/ecs/World'

/**
 * Per-frame visual state of a character rig, produced by the render system.
 *
 * This used to be one number and five booleans, and that was the reason every
 * fighter animated the same way whatever was happening to them: the simulation
 * already knew the cast phase, the launch, the direction of travel and who was
 * nearby, and threw all of it away at this boundary. A rig cannot animate an
 * anticipation it is not told about. See docs/CHARACTERS.md §1.4.
 */
export interface CharacterVisualState {
  /** Horizontal speed, drives the walk cycle rate and forward lean. */
  speed: number
  /** Cumulative distance walked; the step phase is derived from it. */
  gait: number
  /** Facing, in radians. */
  angle: number
  instability: number
  bracing: boolean
  /** Remaining stance time as a fraction, 1 → 0. Drives the shield's decay. */
  braceFraction: number
  dashing: boolean
  stunned: boolean
  casting: number
  /** Which part of a cast is running, or null when not casting. */
  castPhase: 'windup' | 'active' | 'recover' | null
  falling: boolean
  /** True while the fighter is on an authored launch arc. */
  airborne: boolean
  /** Speed the launch started at; drives how violently the figure tumbles. */
  launchSpeed: number
  /** Contacts with the slab so far this launch; each one lands a squash. */
  bounces: number
  /** Pickup shield remaining, in seconds; 0 when inactive. */
  shield: number
  /** Haste remaining, in seconds; 0 when inactive. */
  haste: number
  /** Unit vector toward the nearest opponent, or zeroes when alone. */
  lookX: number
  lookZ: number
  /** This screen's own fighter online; see `OwnMarker`. */
  own: boolean
}

/**
 * The one seam between the simulation and Three.js.
 *
 * Systems hold integer object ids, never meshes. That keeps the ECS free of
 * renderer types and means a different backend could be dropped in by
 * reimplementing this interface.
 */
export interface SceneBridge {
  /**
   * Rebuilds the platform geometry for a fresh round.
   *
   * The arena mesh remembers which slabs have fallen, so without this a round
   * that ended on a collapsed platform started the next one on the wreckage —
   * with fighters walking over holes the simulation considered solid ground.
   */
  resetArena(): void

  addCharacter(player: PlayerId, color: number, kind: CharacterKind): number
  addProp(kind: PropKind, seed: number, scale: number): number
  addVortexField(radius: number, color: number): number
  addHazard(kind: HazardKind, radius: number): number
  addShockwave(color: number): number
  /** The plasma bolt thrown by Push. */
  addBolt(color: number): number
  addPickup(kind: PickupKind): number

  remove(id: number): void
  /**
   * Drops every object not in `alive`. The render sync calls this once per
   * frame with the ids its entities still claim, which is the only way an
   * object created and destroyed inside one fixed step can be reclaimed.
   */
  sweep(alive: ReadonlySet<number>): void
  setTransform(id: number, x: number, y: number, z: number, angle: number): void
  setVisible(id: number, visible: boolean): void
  setScale(id: number, scale: number): void

  updateCharacter(id: number, state: CharacterVisualState, dt: number): void
  /** Vortex/hazard/shockwave visuals need their own animated parameters. */
  updateEffect(id: number, progress: number, intensity: number, dt: number): void
}

export const SceneBridgeResource = defineResource<SceneBridge>('SceneBridge')
